import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Clock, Fingerprint, History, Pencil, Plus, Send, Trash2, Users, XCircle } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { IdentityProviderMark } from "@ncp/assistant-sdk";
import { formatDateTime, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Avatar, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, InlineAlert, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, Switch, cn, notifySaved, toast } from "@ncp/ui";
import { useWorkspaces } from "../workspaces/api";
import { useRoles } from "./api";
import { IdentityProviderEditor } from "./IdentityProviderEditor";
import { useIdentityProviderActions, useIdentityProviderSignIns, useIdentityProviders, type IdentityProvider } from "./identityProviders";

const P = "admin:identity.providers";
const OUTCOME_TONE = { signedIn: "success", refused: "warning", failed: "danger" } as const;
const PREVIEW_ROWS = 6;

function lastTest(p: IdentityProvider): { ok: boolean; message: string } | null {
  if (!p.lastTestResult) return null;
  return { ok: p.lastTestResult.startsWith("OK:"), message: p.lastTestResult.replace(/^(OK|Failed):\s*/, "") };
}

/** Workspace grants are audited as `key` or `key (owner)`. */
function workspaceGrant(value: string): { key: string; owner: boolean } {
  return value.endsWith(" (owner)") ? { key: value.slice(0, -" (owner)".length), owner: true } : { key: value, owner: false };
}

interface MappingPreview { id: string; label: string; roles: string[]; workspaces: Array<{ key: string; owner: boolean }> }

/** A sheet's subject stays set while it closes, so the closing animation keeps showing it. */
interface SheetState { open: boolean; provider: IdentityProvider | null }
const CLOSED: SheetState = { open: false, provider: null };

/** One row per claim value, with everything it grants, for the provider card. */
function previewOf(p: IdentityProvider): MappingPreview[] {
  const byValue = new Map<string, MappingPreview>();
  const entry = (claim: string | null | undefined, value: string, label: string | null | undefined) => {
    const id = `${claim ?? ""}|${value.toLowerCase()}`;
    let row = byValue.get(id);
    if (!row) byValue.set(id, (row = { id, label: label || value, roles: [], workspaces: [] }));
    return row;
  };
  for (const m of p.roleMappings) entry(m.claim, m.value, m.label).roles.push(m.roleKey);
  for (const m of p.workspaceMappings) entry(m.claim, m.value, m.label).workspaces.push({ key: m.workspaceKey, owner: m.isOwner });
  return [...byValue.values()];
}

/** Identity & Access › Identity providers: staff sign-in through the ministry directory, its group mappings, health and sign-in history. */
export function IdentityProvidersPanel({ creating, onCreatingChange }: { creating: boolean; onCreatingChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const canWrite = can("platform.identity.write");
  const providers = useIdentityProviders();
  const actions = useIdentityProviderActions();
  const roles = useRoles();
  const workspaces = useWorkspaces();
  const [editor, setEditor] = useState<SheetState>(CLOSED);
  const [deleting, setDeleting] = useState<IdentityProvider | null>(null);
  const [history, setHistory] = useState<SheetState>(CLOSED);
  const closeEditor = () => { setEditor((s) => ({ ...s, open: false })); onCreatingChange(false); };

  useEffect(() => {
    if (creating) setEditor({ open: true, provider: null });
  }, [creating]);

  const roleName = (k: string) => {
    const r = roles.data?.find((x) => x.key === k);
    return r && locale === "ar" && r.nameAr ? r.nameAr : t(`admin:identity.roles.builtInNames.${k}`, { defaultValue: r?.name ?? k });
  };
  const workspaceName = (k: string) => {
    const w = workspaces.data?.find((x) => x.key === k);
    return w ? (locale === "ar" && w.nameAr ? w.nameAr : w.name) : k;
  };
  const nameOf = (x: IdentityProvider) => (locale === "ar" && x.nameAr ? x.nameAr : x.name);

  if (providers.isLoading) return <Skeleton className="h-72" />;
  if (providers.isError) return <InlineAlert tone="danger" title={t("admin:common.error")}>{providers.error instanceof Error ? providers.error.message : null}</InlineAlert>;
  const rows = providers.data ?? [];

  return (
    <div className="space-y-4">
      <InlineAlert tone="info">{t(`${P}.hint`)}</InlineAlert>

      {rows.length === 0 ? (
        <EmptyState icon={Fingerprint} title={t(`${P}.empty`)} description={t(`${P}.emptyHint`)} action={canWrite ? <Button variant="gold" onClick={() => onCreatingChange(true)}><Plus /> {t(`${P}.newProvider`)}</Button> : undefined} />
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {rows.map((x) => {
            const test = lastTest(x);
            const preview = previewOf(x);
            return (
              <Card key={x.key} className={cn(!x.enabled && "opacity-75", test && !test.ok && "border-danger-200")}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-sand-200 bg-white"><IdentityProviderMark kind={x.kind} iconUrl={x.iconUrl} className="size-5" /></span>
                      <div className="min-w-0">
                        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><span className="truncate">{nameOf(x)}</span>{x.simulated ? <Badge variant="outline" size="sm">{t(`${P}.simulated`)}</Badge> : null}</CardTitle>
                        <CardDescription className="truncate">{t(`${P}.kinds.${x.kind}`)} · <span className="font-mono text-[11px]">{x.key}</span></CardDescription>
                      </div>
                    </div>
                    {canWrite ? (
                      <Switch checked={x.enabled} aria-label={t("admin:common.enabled")}
                        onCheckedChange={(enabled) => actions.setEnabled.mutate({ key: x.key, enabled }, { onSuccess: (answer) => notifySaved(answer, t(enabled ? `${P}.toasts.enabled` : `${P}.toasts.disabled`)) })} />
                    ) : (
                      <Badge variant={x.enabled ? "success" : "outline"} size="sm" dot>{x.enabled ? t("admin:common.enabled") : t("admin:common.disabled")}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs">
                    <dt className="text-muted-foreground">{t(`${P}.fields.issuer`)}</dt>
                    <dd className="truncate font-mono" dir="ltr" title={x.issuer}>{x.issuer}</dd>
                    <dt className="text-muted-foreground">{t(`${P}.fields.clientId`)}</dt>
                    <dd className="truncate font-mono" dir="ltr">{x.clientId}</dd>
                  </dl>

                  <div className="flex flex-wrap gap-1">
                    <Badge variant={x.justInTimeProvisioning ? "info" : "outline"} size="sm">{x.justInTimeProvisioning ? t(`${P}.policy.jit`) : t(`${P}.policy.noJit`)}</Badge>
                    {x.syncOnSignIn ? <Badge variant="outline" size="sm">{t(`${P}.policy.sync`)}</Badge> : null}
                    {x.linkByEmail ? <Badge variant="outline" size="sm">{t(`${P}.policy.link`)}</Badge> : null}
                    {x.allowedEmailDomains.length ? <Badge variant="outline" size="sm" className="font-mono">{t(`${P}.domains`, { domains: x.allowedEmailDomains.join(", ") })}</Badge> : null}
                  </div>

                  <div className="rounded-lg bg-sand-50 p-2.5">
                    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-sand-500">{t(`${P}.mappingsSummary`, { roles: x.roleMappings.length, workspaces: x.workspaceMappings.length })}</p>
                    <ul className="space-y-1">
                      {preview.slice(0, PREVIEW_ROWS).map((m) => (
                        <li key={m.id} className="flex flex-wrap items-center gap-1 text-xs">
                          <span className="truncate font-mono text-sand-700" title={m.label}>{m.label}</span>
                          <ArrowRight className="size-3 shrink-0 text-sand-400 rtl:-scale-x-100" />
                          {m.roles.map((r) => <Badge key={r} variant="gold" size="sm">{roleName(r)}</Badge>)}
                          {m.workspaces.map((w) => <Badge key={w.key} variant="camel" size="sm">{workspaceName(w.key)}{w.owner ? ` · ${t(`${P}.fields.owner`)}` : ""}</Badge>)}
                        </li>
                      ))}
                      {preview.length > PREVIEW_ROWS ? <li className="text-xs text-muted-foreground">+{preview.length - PREVIEW_ROWS}</li> : null}
                    </ul>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Users className="size-3.5" /> {t(`${P}.linkedUsers`, { count: x.linkedUsers })}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="size-3.5" /> {x.lastSignInAt ? t(`${P}.lastSignIn`, { when: formatRelative(x.lastSignInAt, locale) }) : t(`${P}.noSignIns`)}</span>
                    {test ? (
                      <span className={cn("inline-flex items-center gap-1", test.ok ? "text-success-700" : "text-danger-700")} title={test.message}>
                        {test.ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
                        {test.ok ? t(`${P}.testOk`) : t(`${P}.testFailed`)}{x.lastTestedAt ? ` · ${t(`${P}.tested`, { when: formatRelative(x.lastTestedAt, locale) })}` : ""}
                      </span>
                    ) : <span>{t(`${P}.neverTested`)}</span>}
                  </div>
                  {test && !test.ok ? <InlineAlert tone="danger" className="text-xs">{test.message}</InlineAlert> : null}

                  <div className="flex flex-wrap items-center gap-2 border-t border-sand-100 pt-3">
                    <Button size="sm" variant="ghost" onClick={() => setHistory({ open: true, provider: x })}><History /> {t(`${P}.signIns`)}</Button>
                    {canWrite ? (
                      <div className="ms-auto flex items-center gap-1">
                        <Button size="sm" variant="outline" loading={actions.test.isPending && actions.test.variables === x.key}
                          onClick={() => actions.test.mutate(x.key, { onSuccess: (r) => (r.ok ? notifySaved(r, { title: t(`${P}.testOk`), description: r.message }) : toast.warning(t(`${P}.testFailed`), { description: r.message })) })}>
                          <Send /> {t(`${P}.test`)}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditor({ open: true, provider: x })}><Pencil /> {t("admin:common.edit")}</Button>
                        <Button size="sm" variant="ghost" aria-label={t("admin:common.delete")} onClick={() => setDeleting(x)}><Trash2 /></Button>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <IdentityProviderEditor
        open={editor.open}
        provider={editor.provider}
        busy={actions.create.isPending || actions.update.isPending}
        onOpenChange={(open) => { if (!open) closeEditor(); }}
        onSubmit={(key, input, create) => {
          const mutation = create ? actions.create : actions.update;
          mutation.mutate({ key, ...input }, { onSuccess: (answer) => { notifySaved(answer, t(create ? `${P}.toasts.created` : `${P}.toasts.saved`)); closeEditor(); } });
        }}
      />

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t(`${P}.deleteTitle`)}</DialogTitle>
            <DialogDescription>{deleting ? t(`${P}.deleteConfirm`, { name: nameOf(deleting) }) : null}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending}
              onClick={() => deleting && actions.remove.mutate(deleting.key, { onSuccess: (answer) => { notifySaved(answer, t(`${P}.toasts.deleted`)); setDeleting(null); } })}>
              {t("admin:common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignInsSheet open={history.open} provider={history.provider} name={history.provider ? nameOf(history.provider) : ""} onClose={() => setHistory((s) => ({ ...s, open: false }))} roleName={roleName} workspaceName={workspaceName} />
    </div>
  );
}

function SignInsSheet({ open, provider, name, onClose, roleName, workspaceName }: {
  open: boolean;
  provider: IdentityProvider | null;
  name: string;
  onClose: () => void;
  roleName: (key: string) => string;
  workspaceName: (key: string) => string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const signIns = useIdentityProviderSignIns(provider?.key ?? null);
  const list = signIns.data ?? [];
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent width="max-w-xl">
        <SheetHeader className="pe-12">
          <SheetTitle className="flex items-center gap-2"><History className="size-4 text-aegold-700" /> {t(`${P}.signIns`)}</SheetTitle>
          <SheetDescription>{t(`${P}.signInsHint`, { name })}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {signIns.isLoading ? <Skeleton className="h-40" /> : list.length === 0 ? <EmptyState icon={History} title={t(`${P}.noRecentSignIns`)} /> : (
            <ol className="space-y-2">
              {list.map((s, i) => (
                <li key={`${s.at}-${i}`} className="rounded-xl border border-sand-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar name={s.name || s.email || "?"} size="sm" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{s.name || s.email || "—"}</p>
                        {s.email ? <p className="truncate font-mono text-[11px] text-muted-foreground" dir="ltr">{s.email}</p> : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-end">
                      <Badge variant={OUTCOME_TONE[s.outcome]} size="sm" dot>{t(`${P}.outcome.${s.outcome}`)}</Badge>
                      <p className="mt-1 text-[11px] text-muted-foreground" title={formatDateTime(s.at, locale)}>{formatRelative(s.at, locale)}</p>
                    </div>
                  </div>
                  {s.outcome !== "signedIn" && (s.message || s.reason) ? <p className="mt-2 text-xs leading-5 text-danger-700">{s.message || s.reason}</p> : null}
                  {s.roles.length || s.workspaces.length ? (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <span className="me-1 text-[11px] uppercase tracking-wider text-sand-500">{t(`${P}.granted`)}</span>
                      {s.roles.map((r) => <Badge key={r} variant="gold" size="sm">{roleName(r)}</Badge>)}
                      {s.workspaces.map(workspaceGrant).map((w) => <Badge key={w.key} variant="camel" size="sm">{workspaceName(w.key)}{w.owner ? ` · ${t(`${P}.fields.owner`)}` : ""}</Badge>)}
                    </div>
                  ) : null}
                  {s.matched.length ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      <span className="me-1 text-[11px] uppercase tracking-wider text-sand-500">{t(`${P}.matched`)}</span>
                      {s.matched.map((m) => <Badge key={m} variant="outline" size="sm" className="font-mono">{m}</Badge>)}
                    </div>
                  ) : null}
                  {s.created || s.linked ? (
                    <div className="mt-1.5 flex gap-1">
                      {s.created ? <Badge variant="info" size="sm">{t(`${P}.newAccount`)}</Badge> : null}
                      {s.linked ? <Badge variant="outline" size="sm">{t(`${P}.linkedByEmail`)}</Badge> : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
